import React from 'react';
import type { NumberRangeFilter as NumberRangeFilterValue } from '@app-types';
import RangeFilter from './RangeFilter';

export interface NumberRangeFilterProps {
  columnLabel: string;
  value?: NumberRangeFilterValue;
  onChange: (value: NumberRangeFilterValue | null) => void;
}

/** A decimal number, optionally signed — the same form the API accepts. */
const DECIMAL_NUMBER = /^-?\d+(\.\d+)?$/;

const amount = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/** Grouped the way the column's own figures are, so the two compare by eye. */
const money = (value: string) => amount.format(Number(value));

function summariseAmounts(from: string, to: string): string {
  const usable = (value: string) => Boolean(value) && DECIMAL_NUMBER.test(value);
  const left = usable(from) ? money(from) : from;
  const right = usable(to) ? money(to) : to;

  if (from && to) return `${left} – ${right}`;
  if (from) return `From ${left}`;
  if (to) return `Up to ${right}`;
  return '';
}

/**
 * An inclusive numeric range for an amount column.
 *
 * A bound that is not a number, or a maximum below the minimum, is refused with
 * a message rather than sent — the API would answer 400 and the list would empty
 * with nothing on screen explaining why.
 */
const NumberRangeFilter: React.FC<NumberRangeFilterProps> = ({ columnLabel, value, onChange }) => (
  <RangeFilter
    columnLabel={columnLabel}
    inputType="text"
    inputMode="decimal"
    placeholder={{ from: '0.00', to: '0.00' }}
    emptyLabel="Any amount"
    value={value}
    onChange={(bounds) => onChange(bounds && { kind: 'numberRange', ...bounds })}
    validate={(from, to) => {
      for (const [label, bound] of [
        ['Minimum', from],
        ['Maximum', to]
      ] as const) {
        if (bound && !DECIMAL_NUMBER.test(bound)) return `${label} must be a number`;
      }
      return from && to && Number(to) < Number(from)
        ? 'Maximum must not be less than the minimum'
        : null;
    }}
    summarise={summariseAmounts}
  />
);

export default NumberRangeFilter;
